import { alpha, Box, Tooltip, Typography, useTheme } from '@mui/material';

import type { CalculatedFeeKey } from '@/utils/fees/calculatedFeeCatalog';
import { resolveFeeDescription } from '@/utils/fees/feeDescriptions';
import { formatPrice } from '@/utils/helper';

export type StackedFeeItemProps = {
  label: string;
  value: string | number;
  description?: string;
  feeKey?: CalculatedFeeKey;
  /** Fee lines from the admin fee library without a custom description. */
  adminConfigured?: boolean;
};

export function StackedFeeItem({
  label,
  value,
  description,
  feeKey,
  adminConfigured,
}: StackedFeeItemProps) {
  const theme = useTheme();
  const tip = resolveFeeDescription({
    description,
    label,
    feeKey,
    adminConfigured,
  });

  const labelTypography = (
    <Typography
      variant="body2"
      component="span"
      sx={{
        color: theme.palette.text.secondary,
        fontSize: { xs: '0.875rem', sm: '0.9rem' },
        fontWeight: 500,
        mb: 0.5,
        display: 'inline-block',
        cursor: 'help',
        borderBottom: `1px dotted ${alpha(theme.palette.text.secondary, 0.45)}`,
      }}
    >
      {label}
    </Typography>
  );

  const valueTypography = (
    <Typography
      variant="body1"
      component="span"
      sx={{
        textAlign: 'right',
        display: 'block',
        fontSize: { xs: '1rem', sm: '1.1rem' },
        fontWeight: 600,
        color: theme.palette.text.primary,
        cursor: 'help',
      }}
    >
      {typeof value === 'number' ? formatPrice(value) : value}
    </Typography>
  );

  return (
    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
      <Tooltip title={tip} placement="top" arrow enterTouchDelay={0}>
        {labelTypography}
      </Tooltip>

      <Box
        sx={{
          height: 1,
          background: alpha(theme.palette.text.primary, 0.12),
          mb: { xs: 0.75, sm: 1 },
          width: '100%',
        }}
      />

      <Tooltip title={tip} placement="top" arrow enterTouchDelay={0}>
        {valueTypography}
      </Tooltip>
    </Box>
  );
}
